import { useState, useEffect, useRef, forwardRef } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  Chip,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Autocomplete,
  CircularProgress,
  Paper,
  Divider,
  AppBar,
  Toolbar,
  Slide,
  Grid,
  Card,
  CardMedia,
  Fade,
} from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Restore as RestoreIcon,
  RequestQuote,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckCircleIcon,
  Send as SendIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { usePersistentState } from "../../hooks/usePersistentState";
import { useAuth } from "../../context/context";
import { generateTrackingData } from "../../utils/anonymousTracker";
import { useNavigate } from "react-router-dom";
import { useResponsive } from "../../hooks/useResponsive";
import {
  getComunasPorRegion,
  getRegionIdByComuna,
  REGIONES_CHILE_ARRAY,
  COMUNAS_CHILE,
} from "../../constants/comunasChile";

// Transición para el modal fullscreen
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const defaultValues = {
  name: "",
  rut: "",
  email: "",
  phone: "",
  bizModel: "",
  direccion: "",
  regionId: "",
  comunaId: "",
  message: "",
};

// Función para formatear RUT chileno
const formatRUT = (rut) => {
  if (!rut) return "";

  // Limpiar el RUT de caracteres no numéricos excepto 'k' o 'K'
  const cleanRUT = rut.replace(/[^0-9kK]/g, "");

  if (cleanRUT.length === 0) return "";

  // Separar número y dígito verificador
  const rutNumber = cleanRUT.slice(0, -1);
  const verificator = cleanRUT.slice(-1).toUpperCase();

  if (rutNumber.length === 0) return cleanRUT;

  // Formatear número con puntos
  const formattedNumber = rutNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  return `${formattedNumber}-${verificator}`;
};

// Función para validar RUT chileno
const validateRUT = (rut) => {
  if (!rut) return false;

  const cleanRUT = rut.replace(/[^0-9kK]/g, "");

  if (cleanRUT.length < 2) return false;

  const rutNumber = cleanRUT.slice(0, -1);
  const verificator = cleanRUT.slice(-1).toUpperCase();

  // Calcular dígito verificador - ALGORITMO CORRECTO
  let sum = 0;
  let multiplier = 2;

  for (let i = rutNumber.length - 1; i >= 0; i--) {
    sum += parseInt(rutNumber[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const remainder = sum % 11;
  // Fórmula correcta para el dígito verificador chileno:
  //   resto 0 -> "0", resto 1 -> "K" (11-1=10), resto 2..10 -> 11-resto
  const calculatedVerificator =
    remainder === 0 ? "0" : remainder === 1 ? "K" : (11 - remainder).toString();

  return verificator === calculatedVerificator;
};

// Validador para nombre completo (solo texto y números, sin símbolos)
const validateName = (name) => {
  if (!name || !name.trim()) return false;
  // Permite letras, números, espacios, acentos y caracteres especiales del español
  const nameRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ0-9\s]+$/;
  return nameRegex.test(name.trim());
};

// Validador para email
const validateEmail = (email) => {
  if (!email || !email.trim()) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

// Validador para teléfono (+ZZYXXXXXXXX)
const validatePhone = (phone) => {
  if (!phone || !phone.trim()) return false;
  // Formato: +[1-3 dígitos de país][7-10 dígitos]
  const phoneRegex = /^\+\d{1,3}\d{7,10}$/;
  return phoneRegex.test(phone.replace(/\s/g, ""));
};

// Formateador para teléfono
const formatPhone = (phone) => {
  if (!phone) return "";

  // Limpiar todo excepto números y el signo +
  let cleaned = phone.replace(/[^\d+]/g, "");

  // Asegurar que comience con +
  if (!cleaned.startsWith("+")) {
    if (cleaned.startsWith("56")) {
      cleaned = "+" + cleaned;
    } else if (cleaned.length > 0) {
      cleaned = "+56" + cleaned;
    }
  }

  return cleaned;
};

// Validador para giro de empresa (solo texto y números)
const validateBizModel = (bizModel) => {
  if (!bizModel || bizModel.trim() === "") return true; // Campo opcional
  // Permite letras, números, espacios y acentos, pero no símbolos especiales
  const bizModelRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ0-9\s]+$/;
  return bizModelRegex.test(bizModel.trim());
};

// Validador para mensaje (texto, números y símbolos permitidos)
const validateMessage = (message) => {
  if (!message || message.trim() === "") return true; // Campo opcional
  // Permite texto, números, espacios y símbolos comunes de puntuación
  const messageRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ0-9\s.,;:!?¡¿()\-_"'+/@#$%&*]+$/;
  return messageRegex.test(message.trim());
};

// Filtros de entrada para campos
const filterNameInput = (value) => {
  // Remover caracteres no permitidos en tiempo real
  return value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ0-9\s]/g, "");
};

const filterBizModelInput = (value) => {
  // Remover caracteres no permitidos en tiempo real
  return value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ0-9\s]/g, "");
};

const filterMessageInput = (value) => {
  // Remover caracteres no permitidos en tiempo real
  return value.replace(
    /[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ0-9\s.,;:!?¡¿()\-_"'+/@#$%&*]/g,
    ""
  );
};

const QuoteForm = ({ open, onClose, product }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { persistData, retrieveData } = usePersistentState();
  const [formData, setFormData] = useState(defaultValues);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [autoSaveIndicator, setAutoSaveIndicator] = useState(false);
  const [formError, setFormError] = useState(null);
  const [createdQuoteId, setCreatedQuoteId] = useState(null);
  const [createdQuoteNumericId, setCreatedQuoteNumericId] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const { isMobile } = useResponsive();
  const formRef = useRef(null);

  // Clave única para el borrador del producto específico
  const getDraftKey = () => {
    if (user) {
      return `quote-draft-${user.id}-${product?.id || "general"}`;
    }
    return `quote-draft-${product?.id || "general"}`;
  };

  // Función para construir nombre completo del usuario
  const getFullUserName = () => {
    if (!user) return "";

    const nameParts = [user.name, user.lastName, user.secondLastName].filter(
      (part) => part && part.trim() !== ""
    );

    return nameParts.join(" ");
  };

  // Función para obtener valores iniciales del usuario
  const getUserDefaultValues = () => {
    if (!user) return defaultValues;

    const fullName = getFullUserName();
    const formattedRut = user.taxNumber ? formatRUT(user.taxNumber) : "";

    return {
      name: fullName,
      rut: formattedRut,
      email: user.email || "",
      phone: user.phone || "",
      bizModel: "",
      direccion: "",
      regionId: "",
      comunaId: "",
      message: "",
    };
  };

  // 🆕 Cargar perfil completo del usuario con datos de facturación
  const loadUserProfile = async () => {
    if (!user || !token) return;

    try {
      setLoadingProfile(true);
      const { default: productsService } = await import(
        "../../services/productsService"
      );

      const profile = await productsService.user.getProfile(token);

      if (profile) {
        const fullName = [
          profile.name,
          profile.lastName,
          profile.secondLastName,
        ]
          .filter(Boolean)
          .join(" ");
        const formattedRut = profile.taxNumber
          ? formatRUT(profile.taxNumber)
          : "";

        // Obtener comunaId y derivar regionId
        const userComunaId = profile.billingData?.communeId || "";
        const userRegionId = userComunaId
          ? getRegionIdByComuna(userComunaId)
          : "";

        setFormData((prev) => ({
          ...prev,
          name: fullName || prev.name,
          rut: formattedRut || prev.rut,
          email: profile.email || prev.email,
          phone: profile.phone || prev.phone,
          bizModel: profile.billingData?.giro || prev.bizModel,
          direccion:
            profile.billingData?.address || profile.address || prev.direccion,
          regionId: userRegionId || prev.regionId,
          comunaId: userComunaId || prev.comunaId,
        }));

        console.log("✅ Perfil de usuario cargado con datos de facturación:", {
          hasBillingData: !!profile.billingData,
          giro: profile.billingData?.giro,
          communeId: profile.billingData?.communeId,
        });
      }
    } catch (error) {
      console.warn("⚠️ No se pudo cargar el perfil completo:", error.message);
      // No mostrar error al usuario, usar datos básicos del login
    } finally {
      setLoadingProfile(false);
    }
  };

  // Cargar datos del usuario y verificar borrador al abrir el modal
  useEffect(() => {
    if (open && product) {
      const draftKey = getDraftKey();
      const savedDraft = retrieveData(draftKey);

      if (savedDraft && Object.keys(savedDraft).length > 0) {
        // Verificar que el borrador no esté vacío
        const hasContent = Object.values(savedDraft).some(
          (value) => value && value.toString().trim() !== ""
        );

        if (hasContent) {
          setHasDraft(true);
          // Si hay borrador, cargar datos del usuario como base pero no sobrescribir
          const userDefaults = getUserDefaultValues();
          setFormData(userDefaults);
          // Cargar perfil completo del usuario
          loadUserProfile();
          return;
        }
      }

      // Si no hay borrador, cargar datos del usuario automáticamente
      const userDefaults = getUserDefaultValues();
      setFormData(userDefaults);
      // Cargar perfil completo del usuario
      loadUserProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product, user, token]);

  // Auto-guardar borrador mientras el usuario escribe
  useEffect(() => {
    if (open && product) {
      const userDefaults = getUserDefaultValues();

      // Solo guardar como borrador si hay cambios respecto a los datos del usuario
      const hasChanges = Object.keys(formData).some((key) => {
        const currentValue = formData[key]?.toString().trim() || "";
        const userValue = userDefaults[key]?.toString().trim() || "";
        return currentValue !== userValue && currentValue !== "";
      });

      if (hasChanges) {
        const draftKey = getDraftKey();
        persistData(draftKey, {
          ...formData,
          lastSaved: new Date().toISOString(),
          productId: product.id,
          productName: product.name,
        });

        // Mostrar indicador de auto-guardado
        setAutoSaveIndicator(true);
        const timer = setTimeout(() => setAutoSaveIndicator(false), 1500);
        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, open, product, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    // Aplicar filtros y formateo según el campo
    if (name === "rut") {
      processedValue = formatRUT(value);
    } else if (name === "phone") {
      processedValue = formatPhone(value);
    } else if (name === "name") {
      processedValue = filterNameInput(value);
    } else if (name === "bizModel") {
      processedValue = filterBizModelInput(value);
    } else if (name === "message") {
      processedValue = filterMessageInput(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Limpiar errores cuando el usuario corrige
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const loadDraft = () => {
    const draftKey = getDraftKey();
    const savedDraft = retrieveData(draftKey);

    if (savedDraft) {
      // Excluir campos meta del borrador
      const {
        lastSaved: _lastSaved,
        productId: _productId,
        productName: _productName,
      } = savedDraft;
      const draftFormData = { ...savedDraft };
      delete draftFormData.lastSaved;
      delete draftFormData.productId;
      delete draftFormData.productName;
      setFormData({ ...getUserDefaultValues(), ...draftFormData });
      setHasDraft(false);
    }
  };

  const clearDraft = () => {
    const draftKey = getDraftKey();
    persistData(draftKey, {});
    setHasDraft(false);
    // Volver a los datos del usuario en lugar de valores vacíos
    setFormData(getUserDefaultValues());
  };

  const validateForm = () => {
    const newErrors = {};

    // Validar nombre completo
    if (!formData.name.trim()) {
      newErrors.name = "El nombre completo es requerido";
    } else if (!validateName(formData.name)) {
      newErrors.name =
        "El nombre solo puede contener letras, números y espacios";
    }

    // Validar email
    if (!formData.email.trim()) {
      newErrors.email = "El email es requerido";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Formato de email inválido";
    }

    // Validar teléfono
    if (!formData.phone.trim()) {
      newErrors.phone = "El teléfono es requerido";
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = "Formato de teléfono inválido (ej: +56912345678)";
    }

    // Validar RUT
    if (!formData.rut.trim()) {
      newErrors.rut = "El R.U.T es requerido";
    } else if (!validateRUT(formData.rut)) {
      newErrors.rut = "R.U.T chileno inválido";
    }

    // Validar giro de empresa (OBLIGATORIO)
    if (!formData.bizModel.trim()) {
      newErrors.bizModel = "El giro de la empresa es requerido";
    } else if (!validateBizModel(formData.bizModel)) {
      newErrors.bizModel =
        "El giro solo puede contener letras, números y espacios";
    }

    // Validar dirección (OBLIGATORIO)
    if (!formData.direccion.trim()) {
      newErrors.direccion = "La dirección es requerida";
    }

    // Validar región (OBLIGATORIO)
    if (!formData.regionId) {
      newErrors.regionId = "La región es requerida";
    }

    // Validar comuna (OBLIGATORIO)
    if (!formData.comunaId) {
      newErrors.comunaId = "La comuna es requerida";
    }

    // Validar mensaje (opcional)
    if (formData.message.trim() && !validateMessage(formData.message)) {
      newErrors.message = "El mensaje contiene caracteres no permitidos";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Generar ID único para la cotización (formato: QUOTE-YYYY-TIMESTAMP)
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const timestamp = Date.now();
    const quoteId = `QUOTE-${year}-${timestamp}`;

    // Obtener datos de tracking para usuarios anónimos
    const trackingData = generateTrackingData();

    // Formato correcto según la documentación del backend
    const request = {
      id: quoteId,
      tipo: "venta-usados", // Agregar tipo requerido
      producto: {
        ...product, // Incluir todos los datos del producto PRIMERO
        id: product?.id,
        name: product?.name,
        categoria: product?.category || "Vehículos",
        // 💰 PRECIO: Usar price que ahora devuelve el precio efectivo (con desglose si existe)
        precio: product?.price || 0,
      },
      cliente: {
        nombre: formData.name,
        email: formData.email,
        telefono: formData.phone,
        rut: formData.rut,
        direccion: formData.direccion || null,
        comunaId: formData.comunaId || null,
      },
      detalles: {
        empresa: formData.bizModel || null,
        mensaje: formData.message || null,
      },
      estado: "pending",
      // Incluir datos de tracking siguiendo el formato del backend actualizado
      tracking: {
        visitorId: trackingData.visitorId,
        sessionId: trackingData.sessionId,
        browserFingerprint: trackingData.browserFingerprint,
        referrer: trackingData.referrer,
        currentUrl: trackingData.currentUrl,
        // Datos adicionales que van a tracking_data JSONB
        trackingData: {
          userAgent: trackingData.userAgent,
          screenResolution: trackingData.screenResolution,
          timezone: trackingData.timezone,
          language: trackingData.language,
          platform: trackingData.platform,
          timestamp: trackingData.timestamp,
        },
      },
    };

    console.log("📡 Sending quote request:", JSON.stringify(request, null, 2));

    try {
      // Usar la URL actualizada del backend
      const { API_BASE_URL } = await import("../../services/productsService");
      const url = `${API_BASE_URL}/quotes`;

      console.log("📡 Sending quote to updated endpoint:", url);

      // Llamar al endpoint actualizado del backend
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Agregar token de autorización si existe (optionalAuth)
          ...(user?.token && { Authorization: `Bearer ${user.token}` }),
        },
        body: JSON.stringify(request),
      });

      console.log("📡 Response status:", response.status);
      const result = await response.json();
      console.log("📡 Response data:", result);

      // 🔧 NUEVO: Manejo específico de error 401 (no autenticado)
      if (response.status === 401) {
        console.warn("⚠️ Usuario no autenticado - mostrando mensaje");

        setFormError({
          type: "auth",
          message: "Debes iniciar sesión para enviar una cotización",
          details:
            result.message ||
            result.error?.message ||
            "Se requiere autenticación para acceder a este recurso",
        });

        // Scroll al error
        if (formRef.current) {
          formRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }

        setSubmitted(false);
        return;
      }

      // 🔧 MEJORADO: Manejo de errores estructurados del backend
      if (!response.ok || !result.success) {
        // Extraer el mensaje de error según la estructura del backend
        let errorMessage = "Error al enviar la cotización";
        let errorDetails = "";
        let errorType = "general";
        let existingQuoteId = null; // 🆕 NUEVO: Guardar ID de cotización existente

        // El backend puede enviar errores en diferentes formatos:
        // Formato 1: { success: false, error: { code, message, details } }
        // Formato 2: { success: false, error: "string", message: "string" }
        // Formato 3: { error: "string", message: "string" }

        if (result.error) {
          if (typeof result.error === "object") {
            // Formato estructurado: { error: { code, message, details } }
            errorMessage = result.error.message || errorMessage;
            errorDetails = result.error.details || "";

            // Detectar tipo de error según el código
            if (result.error.code === "DUPLICATE_ACTIVE_QUOTE") {
              errorType = "duplicate";

              // Si hay información de la cotización existente
              if (result.data?.existingQuote) {
                const existing = result.data.existingQuote;
                existingQuoteId = existing.quote_id; // 🆕 NUEVO: Guardar el ID

                // No incluir el ID en errorDetails, se mostrará como link
                errorDetails = `Fecha: ${new Date(
                  existing.created_at
                ).toLocaleString("es-CL")}`;
              }

              // Agregar sugerencia si existe
              if (result.meta?.suggestion) {
                errorDetails += `\n\n${result.meta.suggestion}`;
              }
            }
          } else if (typeof result.error === "string") {
            // Formato simple: { error: "string" }
            errorMessage = result.error;
          }
        }

        // Usar message como fallback
        if (!errorMessage || errorMessage === "Error al enviar la cotización") {
          errorMessage = result.message || errorMessage;
        }

        console.warn("⚠️ Error del backend:", {
          message: errorMessage,
          details: errorDetails,
          type: errorType,
          existingQuoteId, // 🆕 NUEVO: Log del ID
          fullResponse: result,
        });

        setFormError({
          type: errorType,
          message: errorMessage,
          details: errorDetails,
          existingQuoteId, // 🆕 NUEVO: Incluir ID en el estado
        });

        // Scroll al error
        if (formRef.current) {
          formRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }

        setSubmitted(false);
        return;
      }

      //si todo ok setSubmitted true
      if (response.ok && result.success) {
        setSubmitted(true);
      }

      // ✅ Solo si todo está OK
      console.log("✅ Quote enviada exitosamente:", result);

      // 🆕 NUEVO: Guardar el ID de la cotización creada
      setCreatedQuoteId(result.data?.quote_id || quoteId);
      setCreatedQuoteNumericId(result.data?.id || null); // 🆕 Guardar ID numérico

      // Limpiar borrador
      const draftKey = getDraftKey();
      persistData(draftKey, {});

      // Resetear formulario a valores del usuario
      setFormData(getUserDefaultValues());
      setErrors({});

      // Mantener submitted=true para mostrar el mensaje de éxito
      // El modal de éxito se encargará de cerrar
    } catch (error) {
      console.error("❌ Error sending quote:", error);

      // 🔧 MEJORADO: Distinguir entre diferentes tipos de errores
      if (
        error.message?.includes("autenticación") ||
        error.message?.includes("No autenticado")
      ) {
        setFormError({
          type: "auth",
          message: "Debes iniciar sesión para enviar una cotización",
          details: "Por favor, inicia sesión o crea una cuenta para continuar.",
        });
      } else if (
        error.message?.includes("Failed to fetch") ||
        error.message?.includes("NetworkError")
      ) {
        setFormError({
          type: "network",
          message: "Error de conexión",
          details:
            "No se pudo conectar con el servidor. Por favor, verifica tu conexión a internet e intenta nuevamente.",
        });
      } else {
        setFormError({
          type: "general",
          message: "Error al enviar la cotización",
          details: error.message || "Por favor, intenta nuevamente más tarde.",
        });
      }

      setSubmitted(false);
    }
  };

  const handleClose = () => {
    onClose();
    setErrors({});
    setSubmitted(false);
    setCreatedQuoteId(null); // 🆕 NUEVO: Resetear ID de cotización
    setCreatedQuoteNumericId(null); // 🆕 NUEVO: Resetear ID numérico
    setDownloadingPdf(false); // 🆕 NUEVO: Resetear estado de descarga
    setPdfError(null); // 🆕 NUEVO: Resetear error de PDF
  };

  // Vista de éxito después de enviar
  if (submitted) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative", bgcolor: "success.main" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              ¡Cotización Enviada!
            </Typography>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "calc(100vh - 64px)",
            p: 3,
            bgcolor: "grey.50",
          }}
        >
          <Fade in timeout={500}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 5 },
                maxWidth: 500,
                width: "100%",
                textAlign: "center",
                borderRadius: 3,
              }}
            >
              <CheckCircleIcon
                sx={{ fontSize: 80, color: "success.main", mb: 2 }}
              />

              <Typography variant="h5" fontWeight="bold" gutterBottom>
                ¡Solicitud Recibida!
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Hemos recibido tu solicitud de cotización para{" "}
                <strong>{product?.name}</strong>. Te contactaremos pronto.
              </Typography>

              {createdQuoteId && (
                <Chip
                  label={`ID: ${createdQuoteId}`}
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 3 }}
                />
              )}

              <Divider sx={{ my: 3 }} />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {/* {createdQuoteId && user?.token && (
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={async () => {
                      try {
                        setDownloadingPdf(true);
                        const { default: productsService } = await import(
                          "../../services/productsService"
                        );
                        await productsService.quotes.downloadAndSavePdf(
                          createdQuoteNumericId,
                          createdQuoteId,
                          user.token
                        );
                        setDownloadingPdf(false);
                      } catch (error) {
                        console.error("Error descargando PDF:", error);
                        setDownloadingPdf(false);
                        setPdfError(
                          "Error al descargar el PDF. Intenta nuevamente."
                        );
                      }
                    }}
                    startIcon={
                      downloadingPdf ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <DescriptionIcon />
                      )
                    }
                    disabled={downloadingPdf}
                  >
                    {downloadingPdf ? "Descargando..." : "Descargar PDF"}
                  </Button>
                )} */}

                {createdQuoteId && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      handleClose();
                      navigate(
                        `/cotizaciones?search=${encodeURIComponent(
                          createdQuoteId
                        )}`
                      );
                    }}
                    startIcon={<RequestQuote />}
                  >
                    Ver Mi Cotización
                  </Button>
                )}

                <Button variant="text" onClick={handleClose} fullWidth>
                  Cerrar
                </Button>
              </Box>

              {pdfError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {pdfError}
                </Alert>
              )}
            </Paper>
          </Fade>
        </Box>
      </Dialog>
    );
  }

  // Formulario principal fullscreen
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      TransitionComponent={Transition}
    >
      {/* Header fijo */}
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Solicitar Cotización
          </Typography>

          {/* Indicadores */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {loadingProfile && (
              <Chip
                icon={<CircularProgress size={16} color="inherit" />}
                label="Cargando..."
                size="small"
                sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
              />
            )}
            {autoSaveIndicator && (
              <Chip
                icon={<SaveIcon />}
                label="Guardado"
                size="small"
                color="success"
              />
            )}
            {hasDraft && (
              <>
                <IconButton
                  size="small"
                  onClick={loadDraft}
                  title="Restaurar borrador"
                  sx={{ color: "white" }}
                >
                  <RestoreIcon />
                </IconButton>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Contenido principal con scroll */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          bgcolor: "grey.100",
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            p: { xs: 2, md: 4 },
          }}
        >
          <Grid container spacing={3}>
            {/* Panel izquierdo: Info del producto */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  position: { md: "sticky" },
                  top: { md: 16 },
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  PRODUCTO A COTIZAR
                </Typography>

                {product?.images?.[0] && (
                  <Card sx={{ mb: 2, borderRadius: 2 }}>
                    <CardMedia
                      component="img"
                      height={isMobile ? 150 : 200}
                      image={product.images[0]}
                      alt={product?.name}
                      sx={{ objectFit: "cover" }}
                    />
                  </Card>
                )}

                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {product?.name}
                </Typography>

                {product?.brand && (
                  <Typography variant="body2" color="text.secondary">
                    Marca: {product.brand}
                  </Typography>
                )}

                {product?.model && (
                  <Typography variant="body2" color="text.secondary">
                    Modelo: {product.model}
                  </Typography>
                )}

                {product?.year && (
                  <Typography variant="body2" color="text.secondary">
                    Año: {product.year}
                  </Typography>
                )}

                {product?.price && (
                  <Typography
                    variant="h6"
                    color="primary"
                    fontWeight="bold"
                    sx={{ mt: 2 }}
                  >
                    {new Intl.NumberFormat("es-CL", {
                      style: "currency",
                      currency: "CLP",
                      maximumFractionDigits: 0,
                    }).format(product.price)}
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="caption" color="text.secondary">
                  Al solicitar una cotización, un ejecutivo se pondrá en
                  contacto contigo para entregarte todos los detalles.
                </Typography>
              </Paper>
            </Grid>

            {/* Panel derecho: Formulario */}
            <Grid size={{ xs: 12, md: 8 }}>
              <form onSubmit={handleSubmit} ref={formRef}>
                {/* Alertas */}
                {hasDraft && (
                  <Alert
                    severity="info"
                    sx={{ mb: 2, borderRadius: 2 }}
                    action={
                      <Box>
                        <Button size="small" onClick={loadDraft}>
                          Restaurar
                        </Button>
                        <Button
                          size="small"
                          onClick={clearDraft}
                          color="inherit"
                        >
                          Descartar
                        </Button>
                      </Box>
                    }
                  >
                    Tienes un borrador guardado para este producto
                  </Alert>
                )}

                {formError && (
                  <Alert
                    severity={
                      formError.type === "auth"
                        ? "warning"
                        : formError.type === "duplicate"
                        ? "info"
                        : "error"
                    }
                    sx={{ mb: 2, borderRadius: 2 }}
                    onClose={() => setFormError(null)}
                    action={
                      formError.type === "auth" ? (
                        <Button
                          size="small"
                          color="inherit"
                          onClick={() => {
                            const draftKey = getDraftKey();
                            persistData(draftKey, {
                              ...formData,
                              lastSaved: new Date().toISOString(),
                              productId: product.id,
                              productName: product.name,
                            });
                            onClose();
                            window.dispatchEvent(
                              new CustomEvent("openLoginModal")
                            );
                          }}
                        >
                          Iniciar Sesión
                        </Button>
                      ) : formError.type === "duplicate" &&
                        formError.existingQuoteId ? (
                        <Button
                          size="small"
                          color="inherit"
                          onClick={() => {
                            onClose();
                            navigate(
                              `/cotizaciones?search=${encodeURIComponent(
                                formError.existingQuoteId
                              )}`
                            );
                          }}
                        >
                          Ver Cotización
                        </Button>
                      ) : null
                    }
                  >
                    <Typography variant="body2" fontWeight="bold">
                      {formError.message}
                    </Typography>
                    {formError.type === "duplicate" &&
                      formError.existingQuoteId && (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => {
                            onClose();
                            navigate(
                              `/cotizaciones?search=${encodeURIComponent(
                                formError.existingQuoteId
                              )}`
                            );
                          }}
                        >
                          {formError.existingQuoteId}
                        </Link>
                      )}
                    {formError.details && (
                      <Typography variant="caption" display="block">
                        {formError.details}
                      </Typography>
                    )}
                  </Alert>
                )}

                {/* Sección: Datos Personales */}
                <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <PersonIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Datos Personales
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        name="name"
                        label="Nombre Completo"
                        value={formData.name}
                        onChange={handleChange}
                        error={!!errors.name}
                        helperText={errors.name}
                        fullWidth
                        required
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        name="rut"
                        label="R.U.T"
                        value={formData.rut}
                        onChange={handleChange}
                        error={!!errors.rut}
                        helperText={errors.rut}
                        fullWidth
                        required
                        placeholder="12.345.678-9"
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        name="email"
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        fullWidth
                        required
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        name="phone"
                        label="Teléfono"
                        value={formData.phone}
                        onChange={handleChange}
                        error={!!errors.phone}
                        helperText={errors.phone}
                        fullWidth
                        required
                        placeholder="+56912345678"
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {/* Sección: Datos de Empresa */}
                <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <BusinessIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Datos de Empresa
                    </Typography>
                  </Box>

                  <TextField
                    name="bizModel"
                    label="Giro de la Empresa"
                    value={formData.bizModel}
                    onChange={handleChange}
                    error={!!errors.bizModel}
                    helperText={errors.bizModel}
                    fullWidth
                    required
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      endAdornment: loadingProfile ? (
                        <CircularProgress size={20} />
                      ) : null,
                    }}
                  />
                </Paper>

                {/* Sección: Ubicación */}
                <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <LocationIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Ubicación
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        name="direccion"
                        label="Dirección"
                        value={formData.direccion}
                        onChange={handleChange}
                        error={!!errors.direccion}
                        helperText={errors.direccion}
                        fullWidth
                        required
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl
                        fullWidth
                        required
                        error={!!errors.regionId}
                        size={isMobile ? "small" : "medium"}
                      >
                        <InputLabel>Región</InputLabel>
                        <Select
                          name="regionId"
                          value={formData.regionId}
                          onChange={(e) => {
                            const newRegionId = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              regionId: newRegionId,
                              comunaId: "",
                            }));
                            if (errors.regionId) {
                              setErrors((prev) => ({ ...prev, regionId: "" }));
                            }
                          }}
                          label="Región"
                        >
                          {REGIONES_CHILE_ARRAY.map((region) => (
                            <MenuItem key={region.id} value={region.id}>
                              {region.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.regionId && (
                          <FormHelperText>{errors.regionId}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Autocomplete
                        options={getComunasPorRegion(formData.regionId)}
                        getOptionLabel={(option) => option.name || ""}
                        value={
                          COMUNAS_CHILE.find(
                            (c) => c.id === formData.comunaId
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          setFormData((prev) => ({
                            ...prev,
                            comunaId: newValue?.id || "",
                          }));
                          if (errors.comunaId) {
                            setErrors((prev) => ({ ...prev, comunaId: "" }));
                          }
                        }}
                        disabled={!formData.regionId}
                        size={isMobile ? "small" : "medium"}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Comuna"
                            error={!!errors.comunaId}
                            helperText={
                              errors.comunaId ||
                              (!formData.regionId
                                ? "Selecciona una región primero"
                                : "")
                            }
                            required
                          />
                        )}
                        fullWidth
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        noOptionsText="No hay comunas disponibles"
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {/* Sección: Mensaje Adicional */}
                <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <DescriptionIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Mensaje Adicional
                    </Typography>
                    <Chip
                      label="Opcional"
                      size="small"
                      sx={{ ml: 1 }}
                      variant="outlined"
                    />
                  </Box>

                  <TextField
                    name="message"
                    label="Mensaje de cotización"
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    fullWidth
                    placeholder="Especificaciones adicionales, condiciones especiales, preguntas, etc."
                    size={isMobile ? "small" : "medium"}
                  />
                </Paper>

                {/* Botones de acción */}
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    position: "sticky",
                    bottom: 0,
                    bgcolor: "background.paper",
                    zIndex: 10,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexDirection: isMobile ? "column" : "row",
                      gap: 2,
                    }}
                  >
                    <Button
                      onClick={handleClose}
                      variant="outlined"
                      fullWidth={isMobile}
                      size="large"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth={isMobile}
                      startIcon={<SendIcon />}
                      sx={{
                        minWidth: { sm: 250 },
                        py: 1.5,
                      }}
                    >
                      Enviar Solicitud de Cotización
                    </Button>
                  </Box>
                </Paper>
              </form>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Dialog>
  );
};

export default QuoteForm;
